/**
 * K线图与柱状图组合验证配置
 * 为新 ValidationEngine 提供验证规则
 */

import type { ValidationConfig } from '../../../core/dataProcessor/types';

// 类型定义
interface Stock {
  code?: string;
  market?: string;
}

interface HXKLineLayer {
  stocks?: Stock[];
}

// 🆕 为新 ValidationEngine 导出的配置
export const hxKLineAndBarValidationConfig: ValidationConfig = {
  fields: [
    { field: 'x', type: 'date', required: true },
    { field: 'y', type: 'number', required: true }
  ],
  businessRules: [
    {
      name: 'hx-kline-and-bar-stocks-required',
      validator: (data, layer) => {
        const hxKLineLayer = layer as HXKLineLayer;
        return Array.isArray(hxKLineLayer.stocks) && hxKLineLayer.stocks.length > 0;
      },
      message: 'K-line and bar chart requires stock configuration',
      suggestion: 'Provide stocks array with at least one valid stock configuration',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-bar-stock-market-required',
      validator: (data, layer) => {
        const hxKLineLayer = layer as HXKLineLayer;
        if (!Array.isArray(hxKLineLayer.stocks)) {
          return true;
        }

        return hxKLineLayer.stocks.every((stock: Stock) => Boolean(stock.code && stock.market));
      },
      message: 'All stocks must have valid code and market properties',
      suggestion: 'Ensure each stock has both code and market properties defined',
      severity: 'error'
    },
    {
      name: 'hx-kline-and-bar-sufficient-data',
      validator: data => Array.isArray(data) && data.length >= 2,
      message: 'K-line and bar chart requires at least 2 data points',
      suggestion: 'Provide at least 2 data points for meaningful K-line and volume visualization',
      severity: 'warning'
    },
    {
      name: 'hx-kline-and-bar-date-continuity',
      validator: (data, layer) => {
        if (!Array.isArray(data)) {
          return false;
        }
        const { encoding } = layer;
        if (data.length < 2) {
          return true;
        }

        // 检查日期序列是否连续（按时间排序）
        const dates = data
          .map(item => new Date(item[encoding?.x as string]))
          .sort((a, b) => a.getTime() - b.getTime());
        return dates.every(date => !Number.isNaN(date.getTime()));
      },
      message: 'K-line chart requires valid date sequence',
      suggestion: 'Ensure all x-field values are valid dates for proper time series visualization',
      severity: 'warning'
    }
  ]
};
